// The picker for a setting that names one of the organisation's tax categories (`x-clavian-control: taxCategory`;
// F-PAY-11) — `tax.customLineCategory`, the category a custom time or other line at Sell is taxed under. Empty is
// the standard 18% for services with no HSN/SAC code: what every custom line had before the setting.
//
// The value is the category's TypeID (`txc_…`), or null. An archived category stays listed while it is the value,
// so the screen never shows a value it cannot name.

import type { TaxCategory } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { SelectField } from '@clavian/ui-web';
import type { ComponentType, ReactNode } from 'react';

import { percentOf, useTaxCategories } from './tax-categories';
import type { ControlProps } from './types';

/** The empty choice's value: a select option needs a string, and the setting stores null for it. */
const STANDARD = '';

function TaxCategoryPicker({ organizationId, ...props }: ControlProps & { readonly organizationId: string }): ReactNode {
  const { t } = useTranslation('console');
  const categories = useTaxCategories(organizationId);
  const value = typeof props.value === 'string' ? props.value : STANDARD;
  const listed = (categories.data ?? []).filter((category: TaxCategory) => !category.archived || category.id === value);
  const options = [
    { value: STANDARD, label: t('taxCategories.standardRate') },
    ...listed.map((category) => ({ value: category.id, label: t('taxCategories.option', { name: category.name, rate: percentOf(category.rateBps) }) })),
  ];
  return (
    <SelectField
      label={props.label}
      description={props.description}
      error={props.errorAt(props.path)}
      disabled={props.disabled || categories.data === undefined}
      options={options}
      value={value}
      onValueChange={(next) => props.onChange(next === STANDARD ? null : next)}
    />
  );
}

const controls = new Map<string, Readonly<Record<string, ComponentType<ControlProps>>>>();

/** The custom controls for an organisation's settings screens — one stable set per organisation, so a re-render keeps them. */
export function taxCategoryControlFor(organizationId: string): Readonly<Record<string, ComponentType<ControlProps>>> {
  let found = controls.get(organizationId);
  if (found === undefined) {
    const Control = (props: ControlProps): ReactNode => <TaxCategoryPicker {...props} organizationId={organizationId} />;
    found = { taxCategory: Control };
    controls.set(organizationId, found);
  }
  return found;
}
